import instance from "../interceptor";
import { ErrorResponse } from "../types";
import { CustomRangeReportArgs, TaxYearReportArgs } from "./types";

const FILENAME_PATTERN = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i;

const composeCustomRangeFallbackFileName = ({
  startDate,
  endDate,
}: CustomRangeReportArgs) => `financial-report_${startDate}_${endDate}.pdf`;

const composeTaxYearFallbackFileName = ({ taxYear }: TaxYearReportArgs) =>
  `financial-report_tax-year_${taxYear}.pdf`;

// Content-Disposition is only readable same-origin (prod, behind nginx); a cross-origin dev setup hides it via CORS, so fall back to the name the backend composes.
const resolveFileName = (
  contentDisposition: unknown,
  fallbackFileName: string,
): string => {
  if (typeof contentDisposition !== "string") {
    return fallbackFileName;
  }

  const match = FILENAME_PATTERN.exec(contentDisposition);
  if (!match) {
    return fallbackFileName;
  }

  return decodeURIComponent(match[1]);
};

// responseType: "blob" wraps even 4xx/5xx bodies in a Blob, so getErrorMessages would see no `errors` array — read it back so real messages survive.
const normaliseBlobError = async (error: unknown): Promise<unknown> => {
  if (!(error instanceof Blob)) {
    return error;
  }

  const text = await error.text();

  try {
    const parsed = JSON.parse(text) as ErrorResponse;
    if (parsed?.errors) {
      return parsed;
    }
    return { errors: [text] } as ErrorResponse;
  } catch {
    // Not JSON — the rate limiter answers 429 with a bare text/html string.
    return { errors: [text] } as ErrorResponse;
  }
};

interface DownloadReportPdfParams {
  url: string;
  params: Record<string, string | number>;
  fallbackFileName: string;
}

// Off RTK Query on purpose (axiosBaseQuery doesn't forward responseType: "blob", and a download isn't cached server state) — this also bypasses errorToastMiddleware, so callers must catch and toast.
const downloadReportPdf = async ({
  url,
  params,
  fallbackFileName,
}: DownloadReportPdfParams): Promise<void> => {
  let response;
  try {
    response = await instance.get(url, { params, responseType: "blob" });
  } catch (error: unknown) {
    throw await normaliseBlobError(error);
  }

  const fileName = resolveFileName(
    response.headers?.["content-disposition"],
    fallbackFileName,
  );

  const objectUrl = globalThis.window.URL.createObjectURL(
    new Blob([response.data]),
  );
  const link = document.createElement("a");
  link.href = objectUrl;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.window.URL.revokeObjectURL(objectUrl);
};

export const downloadCustomRangeReportPdf = async (
  args: CustomRangeReportArgs,
): Promise<void> => {
  await downloadReportPdf({
    url: "/reports/custom/pdf",
    params: { startDate: args.startDate, endDate: args.endDate },
    fallbackFileName: composeCustomRangeFallbackFileName(args),
  });
};

// Unlike the custom-range PDF, this one carries the additive "Savings and investments" section built from the tax-year returns contract.
export const downloadTaxYearReportPdf = async (
  args: TaxYearReportArgs,
): Promise<void> => {
  await downloadReportPdf({
    url: "/reports/tax-year/pdf",
    params: { taxYear: args.taxYear },
    fallbackFileName: composeTaxYearFallbackFileName(args),
  });
};
